import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import patientService from '../../services/patientService';
import VitalsTabs from '../../components/MeasurementData/VitalsTabs/VitalsTabs';
import AddFollowUpModal from '../../components/AddFollowUpModal/AddFollowUpModal';
import PatientDemographicsBanner from '../PracticeManagement/PatientManagement/PatientDemographicsBanner';
import {
  buildPatientFullName,
  enrichPatientForDemographicsBanner,
  formatNameLastFirst,
  getGenderText,
} from './measurementPatientDemographics';
import '../PracticeManagement/PatientManagement/PatientDetails.css';
import './MeasurementShell.css';
import {
  createFreshProgramTimers,
  getPatientTimerStorageKey,
  loadPersistedTimers,
  persistTimers,
  finalizePatientSessionTimers,
  resolveActiveProgramTimerKey
} from '../../utils/patientBillingUtils';

const MeasurementSessionContext = createContext(null);

export const useMeasurementSession = () => {
  const ctx = useContext(MeasurementSessionContext);
  if (!ctx) {
    throw new Error('useMeasurementSession must be used within MeasurementControlsBar');
  }
  return ctx;
};

export const useMeasurementSessionOptional = () => useContext(MeasurementSessionContext);

const formatTimer = (totalSeconds) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, '0')} : ${secs.toString().padStart(2, '0')}`;
};

const readSharedSession = (storageKey, routeProgram) => {
  const loaded = loadPersistedTimers(storageKey);
  const programKey = resolveActiveProgramTimerKey({
    routeProgram,
    storedProgram: loaded.activeProgram,
    timers: loaded
  });
  const active = loaded[programKey] || { seconds: 0, isRunning: false };
  const programTimers = {
    RPM: { seconds: loaded.RPM.seconds, isRunning: loaded.RPM.isRunning },
    CCM: { seconds: loaded.CCM.seconds, isRunning: loaded.CCM.isRunning },
    PCM: { seconds: loaded.PCM?.seconds || 0, isRunning: Boolean(loaded.PCM?.isRunning) }
  };
  const hasStoredTime = active.seconds > 0
    || active.isRunning
    || programTimers.RPM.seconds > 0
    || programTimers.CCM.seconds > 0
    || programTimers.PCM.seconds > 0
    || programTimers.RPM.isRunning
    || programTimers.CCM.isRunning
    || programTimers.PCM.isRunning;

  return {
    programKey,
    programTimers,
    seconds: active.seconds,
    // Resume if the shared session was running; otherwise auto-start a fresh measurement session.
    isRunning: hasStoredTime ? Boolean(active.isRunning) : true
  };
};

/**
 * Shared Measurement Controls — stays mounted across BP/BG/Weight so tab pill slides smoothly.
 * Session timer is shared with patient profile via sessionStorage (no pause-before-navigate needed).
 */
const MeasurementControlsBar = ({
  practiceId,
  patientId,
  practiceIsLocked = false,
  activeVital = 'blood-pressure',
  vitalsMode = 'measurement',
  children
}) => {
  const location = useLocation();
  const timerStorageKey = getPatientTimerStorageKey(practiceId, patientId);
  const routeProgram = location.state?.activeProgram;

  const bootRef = useRef(null);
  if (!bootRef.current || bootRef.current.storageKey !== timerStorageKey) {
    const session = practiceIsLocked
      ? {
          programKey: 'RPM',
          programTimers: createFreshProgramTimers(),
          seconds: 0,
          isRunning: false
        }
      : readSharedSession(timerStorageKey, routeProgram);
    bootRef.current = { storageKey: timerStorageKey, session };
  }

  const [programKey, setProgramKey] = useState(bootRef.current.session.programKey);
  const [timer, setTimer] = useState(bootRef.current.session.seconds);
  const [isRunning, setIsRunning] = useState(bootRef.current.session.isRunning);
  const [patientData, setPatientData] = useState(null);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const followUpWasRunningRef = useRef(false);
  const programTimersRef = useRef(bootRef.current.session.programTimers);
  const sessionRef = useRef({
    programKey: bootRef.current.session.programKey,
    timer: bootRef.current.session.seconds,
    isRunning: bootRef.current.session.isRunning
  });

  useEffect(() => {
    sessionRef.current = { programKey, timer, isRunning };
  }, [programKey, timer, isRunning]);

  // Re-hydrate when switching patients; flush on leave so profile can resume mid-run.
  useEffect(() => {
    if (practiceIsLocked) {
      programTimersRef.current = createFreshProgramTimers();
      setProgramKey('RPM');
      setTimer(0);
      setIsRunning(false);
      return undefined;
    }

    const session = readSharedSession(timerStorageKey, routeProgram);
    programTimersRef.current = session.programTimers;
    setProgramKey(session.programKey);
    setTimer(session.seconds);
    setIsRunning(session.isRunning);

    return () => {
      const latest = sessionRef.current;
      const nextTimers = {
        ...programTimersRef.current,
        [latest.programKey]: {
          seconds: latest.timer,
          isRunning: latest.isRunning
        }
      };
      // Keep timer only within this patient session; clear when leaving to patients list.
      finalizePatientSessionTimers(
        timerStorageKey,
        {
          ...nextTimers,
          activeProgram: latest.programKey
        },
        { practiceId, patientId }
      );
    };
  }, [timerStorageKey, practiceIsLocked, practiceId, patientId]); // eslint-disable-line react-hooks/exhaustive-deps -- routeProgram captured on patient change

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') setPatientData(cached);
      } catch {
        // ignore
      }
    }
    (async () => {
      try {
        const response = await patientService.getPatientDetailsFast(practiceId, patientId);
        if (cancelled) return;
        if (response.data.success) {
          const next = response.data.data;
          setPatientData(next);
          sessionStorage.setItem(cacheKey, JSON.stringify(next));
        }
      } catch (error) {
        console.error('Error fetching patient data for controls:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!isRunning || practiceIsLocked) return undefined;
    const interval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, practiceIsLocked]);

  // Keep shared storage in sync so profile can resume without an explicit pause.
  useEffect(() => {
    if (practiceIsLocked) return;
    const nextTimers = {
      ...programTimersRef.current,
      [programKey]: {
        seconds: timer,
        isRunning
      }
    };
    programTimersRef.current = nextTimers;
    persistTimers(timerStorageKey, {
      ...nextTimers,
      activeProgram: programKey
    });
  }, [timer, isRunning, programKey, timerStorageKey, practiceIsLocked]);

  const bannerPatient = useMemo(
    () => enrichPatientForDemographicsBanner(patientData),
    [patientData]
  );
  const patientName = buildPatientFullName(bannerPatient);
  const patientVitals = bannerPatient?.vitals || '';
  const activeProgram =
    location.state?.activeProgram || programKey || 'RPM';
  const deviceSerial = patientData?.device_serial || bannerPatient?.device_serial;
  const consentDate =
    bannerPatient?.ccm_consent_date ||
    patientData?.ccm_enrollment_summary?.consent_date ||
    null;

  const timerAside = !practiceIsLocked ? (
    <div className="bp-timer-block bp-timer-block--in-banner" data-export-ignore="true">
      <span className="bp-timer-label">Active Session</span>
      <div className="bp-timer-value-row">
        <span className="bp-timer-value">{formatTimer(timer)}</span>
        <div className="bp-timer-actions">
          <button
            type="button"
            className={`bp-timer-btn bp-timer-toggle ${isRunning ? 'running' : 'paused'}`}
            onClick={() => setIsRunning((prev) => !prev)}
            title={isRunning ? 'Pause Timer' : 'Start Timer'}
            aria-label={isRunning ? 'Pause Timer' : 'Start Timer'}
          >
            <FontAwesomeIcon icon={isRunning ? faPause : faPlay} />
          </button>
          <button
            type="button"
            className="bp-timer-btn bp-timer-close"
            onClick={() => {
              setIsRunning(false);
              setTimer(0);
            }}
            title="Reset Timer"
            aria-label="Reset Timer"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const sessionValue = useMemo(
    () => ({
      timer,
      setTimer,
      isRunning,
      setIsRunning,
      formatTimer,
      practiceIsLocked,
      patientData
    }),
    [timer, isRunning, practiceIsLocked, patientData]
  );

  return (
    <MeasurementSessionContext.Provider value={sessionValue}>
      <div className="bp-section bp-section--controls">
        <div className="bp-controls-header bp-controls-header--full">
          <div className="measurement-controls-demo">
            <PatientDemographicsBanner
              patient={bannerPatient || {}}
              patientFullName={patientName}
              getGenderText={getGenderText}
              formatNameLastFirst={formatNameLastFirst}
              deviceSerial={deviceSerial}
              consentDate={consentDate}
              activeProgram={activeProgram}
              showContactActions={false}
              bannerClassName="pd-demo-banner--measurement"
              bannerAside={timerAside}
            />
          </div>
        </div>
        <div className="bp-controls-nav" data-export-ignore="true">
          <VitalsTabs
            practiceId={practiceId}
            patientId={patientId}
            vitals={patientVitals}
            active={activeVital}
            mode={vitalsMode}
            className="bp-controls-vitals-tabs"
          />
          {vitalsMode !== 'chart' && !practiceIsLocked && (
            <button
              className="bp-add-followup-btn"
              type="button"
              onClick={() => {
                followUpWasRunningRef.current = isRunning;
                setIsRunning(false);
                setShowFollowUpModal(true);
              }}
            >
              Add Follow Up
            </button>
          )}
        </div>
      </div>

      {children ? (
        <div
          className={
            vitalsMode === 'chart' ? 'measurement-chart-content' : 'measurement-data-content'
          }
        >
          {children}
        </div>
      ) : null}

      {vitalsMode !== 'chart' && !practiceIsLocked && showFollowUpModal && (
        <AddFollowUpModal
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          practiceId={practiceId}
          patientId={patientId}
          patientName={patientName}
          initialTimerValue={timer}
          onTimerSync={(seconds) => {
            setTimer(Math.max(0, Number(seconds) || 0));
            setIsRunning(true);
            followUpWasRunningRef.current = false;
          }}
          onSuccess={() => {}}
        />
      )}
    </MeasurementSessionContext.Provider>
  );
};

MeasurementControlsBar.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  practiceIsLocked: PropTypes.bool,
  activeVital: PropTypes.oneOf(['blood-pressure', 'blood-glucose', 'weight']),
  vitalsMode: PropTypes.oneOf(['measurement', 'chart']),
  children: PropTypes.node
};

export default MeasurementControlsBar;
